import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function AuthBridge() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [errorMessage, setErrorMessage] = useState("");
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (hasAttempted.current) return;
    
    const token = searchParams.get("token");
    
    if (!token) {
      setStatus("error");
      setErrorMessage("No authentication token provided");
      return;
    }

    hasAttempted.current = true;
    
    // Decode token payload to extract trial information
    try {
      const parts = token.split('.');
      if (parts.length === 2) {
        let base64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        if (pad === 2) base64 += '==';
        else if (pad === 3) base64 += '=';
        
        const payloadStr = atob(base64);
        const payload = JSON.parse(payloadStr);
        
        if (payload.is_trial !== undefined) {
          localStorage.setItem('billing_is_trial', payload.is_trial ? 'true' : 'false');
          localStorage.setItem('billing_trial_days', String(payload.trial_days_left || 0));
        }
      }
    } catch (e) {
      console.warn("Failed to parse token payload for trial data", e);
    }

    verifyTokenAndLogin(token);
  }, [searchParams]);

  const verifyTokenAndLogin = async (token: string) => {
    try {
      setStatus("verifying");

      // Call Supabase Edge Function to verify SETU Suvidha token
      const { data, error } = await supabase.functions.invoke("verify-setu-token", {
        body: { token },
      });

      if (error) {
        throw new Error(error.message || "Token verification failed");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.session) {
        throw new Error("No session returned from verification");
      }

      // Manually set the Supabase session with returned tokens
      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      if (setSessionError) {
        throw new Error("Failed to establish session: " + setSessionError.message);
      }

      setStatus("success");
      
      toast({
        title: "Authentication successful",
        description: "Redirecting to billing dashboard...",
      });

      // Redirect to dashboard after brief delay
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);

    } catch (error: any) {
      console.error("Auth bridge error:", error);
      setStatus("error");
      setErrorMessage(error.message || "Authentication failed");
      
      toast({
        title: "Authentication failed",
        description: error.message || "Unable to verify your session",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {status === "verifying" && "Connecting to Billing"}
            {status === "success" && "Authentication Successful"}
            {status === "error" && "Authentication Failed"}
          </CardTitle>
          <CardDescription>
            {status === "verifying" && "Verifying your session from SETU Suvidha..."}
            {status === "success" && "Redirecting you to the billing dashboard"}
            {status === "error" && "Unable to connect your account"}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {status === "verifying" && (
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          )}
          {status === "success" && (
            <CheckCircle className="h-12 w-12 text-green-500" />
          )}
          {status === "error" && (
            <>
              <XCircle className="h-12 w-12 text-destructive" />
              {errorMessage && (
                <p className="text-sm text-muted-foreground text-center">
                  {errorMessage}
                </p>
              )}
              <p className="text-sm text-center">
                Please return to SETU Suvidha and try again, or contact support if the issue persists.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
